import Editor from "@monaco-editor/react";
import { Box, Typography } from "@mui/material";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STRUCTURED_PROMPT_VIEW_MODES } from "../../../../constants/FiboConstants";
import { ThemeMode } from "../../../../constants/themes";
import { useTheme } from "../../../../context/ThemeContext";
import { useAppStore } from "../../../../hooks/useStores";
import { JSONValue } from "../../../../models/fibo";
import { updateStructuredPromptValue } from "../../../../utils/fiboLabs";
import FiboPromptTree from "../FiboPromptTree/FiboPromptTree";
import styles from "./StructuredPromptView.module.scss";

type StructuredPromptViewProps = {
	data: JSONValue | null | undefined;
	previousData?: JSONValue | null;
	deltaPrompt?: string | null;
	height?: number | string;
	viewMode?: STRUCTURED_PROMPT_VIEW_MODES;
	isEditable?: boolean;
	textProps: {
		wordCountText: string;
		emptyStateTitle: string;
		emptyStateDescription: string;
	};
	onStructuredPromptChange?: (data: JSONValue) => void;
};

const StructuredPromptView = ({
	data,
	previousData,
	deltaPrompt,
	height,
	viewMode,
	isEditable,
	textProps,
	onStructuredPromptChange,
}: StructuredPromptViewProps) => {
	const { mode } = useTheme();
	const { fiboStore } = useAppStore();
	const [editableData, setEditableData] = useState<JSONValue | null | undefined>(data);
	const [userEditedJson, setUserEditedJson] = useState<string>("");
	const isInternalChange = useRef(false);

	useEffect(() => {
		if (!isInternalChange.current) setEditableData(data);
		isInternalChange.current = false;
	}, [data]);

	const jsonText = useMemo(() => JSON.stringify(editableData ?? null, null, 2), [editableData]);
	const displayJsonText =
		viewMode === STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE && userEditedJson ? userEditedJson : jsonText;
	const numberOfWords = useMemo(() => jsonText.split(" ").length, [jsonText]);

	const RenderedWords = useMemo(
		() => (
			<Typography className={styles.wordCount}>
				{numberOfWords} {textProps.wordCountText}
			</Typography>
		),
		[numberOfWords],
	);

	const debouncedOnChange = useMemo(
		() => (onStructuredPromptChange ? debounce(onStructuredPromptChange, 100) : undefined),
		[onStructuredPromptChange],
	);

	useEffect(() => () => debouncedOnChange?.cancel(), [debouncedOnChange]);

	// Let generate/refine flush a pending tree edit before building the payload.
	useEffect(() => {
		if (!debouncedOnChange) return;
		return fiboStore.registerVglFlush(() => debouncedOnChange.flush());
	}, [debouncedOnChange, fiboStore]);

	const handleValueChange = useCallback(
		(path: string[], value: JSONValue) => {
			setEditableData((prev) => {
				if (!prev) return prev;
				const updated = updateStructuredPromptValue(prev, path, value);
				isInternalChange.current = true;
				debouncedOnChange?.(updated);
				return updated;
			});
		},
		[debouncedOnChange],
	);

	if (viewMode === STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE) {
		return (
			<Box className={styles.structuredPromptContainer}>
				<Box className={styles.editorContainer}>
					<Editor
						key="json-source-editor"
						height="100%"
						defaultLanguage="json"
						theme={mode === ThemeMode.Dark || mode === ("labs" as ThemeMode) ? "vs-dark" : "light"}
						value={displayJsonText}
						onChange={(v) => setUserEditedJson(v ?? "")}
						options={{
							lineNumbers: "on",
							minimap: { enabled: false },
							wordWrap: "off",
							tabSize: 4,
							fontSize: 14,
							readOnly: true,
							lineDecorationsWidth: 0,
							glyphMargin: false,
							lineNumbersMinChars: 0,
						}}
					/>
				</Box>
				{RenderedWords}
			</Box>
		);
	}

	if (viewMode === STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT && deltaPrompt) {
		return (
			<Box className={styles.structuredPromptContainer}>
				<Box className={styles.editorContainer}>
					<Editor
						key="delta-prompt-editor"
						height="100%"
						defaultLanguage="plaintext"
						theme={mode === ThemeMode.Dark || mode === ("labs" as ThemeMode) ? "vs-dark" : "light"}
						value={deltaPrompt}
						options={{
							lineNumbers: "off",
							minimap: { enabled: false },
							wordWrap: "on",
							tabSize: 4,
							fontSize: 14,
							readOnly: true,
							lineDecorationsWidth: 0,
						}}
					/>
				</Box>
			</Box>
		);
	}

	if (viewMode === STRUCTURED_PROMPT_VIEW_MODES.STRUCTURE) {
		const treeData = (editableData ?? null) as JSONValue;
		return (
			<Box className={styles.structuredPromptContainer}>
				<Box className={styles.treeContainer} style={{ height }}>
					<FiboPromptTree
						data={treeData}
						showPreserved={!!previousData}
						diff={{ previous: previousData ?? null, current: treeData }}
						isEditable={isEditable}
						onValueChange={handleValueChange}
					/>
				</Box>
				{RenderedWords}
			</Box>
		);
	}

	return (
		<div className={styles.emptyState}>
			<Typography className={styles.title} variant="h6">
				{textProps.emptyStateTitle}
			</Typography>
			<Typography className={styles.description} variant="body1">
				{textProps.emptyStateDescription}
			</Typography>
		</div>
	);
};

export default observer(StructuredPromptView);
